/**
 * System Settings Service
 *
 * Fetches system settings from Supabase for feature flags and configuration.
 * Settings are cached in memory to avoid repeated API calls.
 */

import { supabase } from '@/lib/supabase';

// Cache for system settings
let settingsCache: Record<string, unknown> | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Settings keys we need in the user app
const REQUIRED_SETTINGS = [
  'oauth_google_enabled',
  'oauth_apple_enabled',
  'deeplinks_enabled',
  'app_maintenance_mode',
  'push_notifications_enabled',
  'show_wissenswertes',
  'show_activity_feed',
  'activity_feed_enabled_types',
  'activity_feed_dashboard_count',
] as const;

type SettingKey = (typeof REQUIRED_SETTINGS)[number];

/**
 * Fetch system settings from Supabase
 * Results are cached for CACHE_TTL milliseconds
 */
export async function fetchSystemSettings(): Promise<Record<string, unknown>> {
  // Return cached settings if still valid
  if (settingsCache && Date.now() - cacheTimestamp < CACHE_TTL) {
    return settingsCache;
  }

  try {
    const { data, error } = await supabase
      .from('system_settings')
      .select('key, value')
      .in('key', REQUIRED_SETTINGS);

    if (error) {
      console.error('Error fetching system settings:', error);
      // Return cached settings if available, even if expired
      return settingsCache || {};
    }

    // Convert array to key-value map
    const settings: Record<string, unknown> = {};
    data?.forEach((item: { key: string; value: unknown }) => {
      settings[item.key] = item.value;
    });

    // Update cache
    settingsCache = settings;
    cacheTimestamp = Date.now();

    return settings;
  } catch (error) {
    console.error('Error fetching system settings:', error);
    return settingsCache || {};
  }
}

/**
 * Get a specific setting value
 */
export async function getSetting<T>(
  key: SettingKey,
  defaultValue: T
): Promise<T> {
  const settings = await fetchSystemSettings();
  const value = settings[key];
  return value !== undefined ? (value as T) : defaultValue;
}

/**
 * Check if Google OAuth is enabled in system settings
 */
export async function isGoogleOAuthEnabled(): Promise<boolean> {
  return getSetting('oauth_google_enabled', false);
}

/**
 * Check if Apple OAuth is enabled in system settings
 */
export async function isAppleOAuthEnabled(): Promise<boolean> {
  return getSetting('oauth_apple_enabled', false);
}

/**
 * Check if DeepLinks/App redirects are enabled in system settings
 * When disabled, the Smart App Banner won't show and links stay on web
 */
export async function isDeepLinksEnabled(): Promise<boolean> {
  return getSetting('deeplinks_enabled', false);
}

/**
 * Check if Wissenswertes section should be shown
 */
export async function isWissenswertesEnabled(): Promise<boolean> {
  return getSetting('show_wissenswertes', true);
}

/**
 * Check if Activity Feed section should be shown
 */
export async function isActivityFeedEnabled(): Promise<boolean> {
  return getSetting('show_activity_feed', true);
}

/**
 * Get enabled activity types for the feed
 */
export async function getActivityFeedEnabledTypes(): Promise<string[]> {
  return getSetting('activity_feed_enabled_types', [
    'pregnancy_created',
    'pregnancy_birth',
    'pregnancy_ended',
    'ssw_milestone',
    'partner_connected',
    'checklist_item_completed',
    'name_match',
    'appointment_created',
  ]);
}

/**
 * Get number of activities to show in dashboard widget
 */
export async function getActivityFeedDashboardCount(): Promise<number> {
  return getSetting('activity_feed_dashboard_count', 5);
}

/**
 * Clear the settings cache
 * Call this when settings might have changed
 */
export function clearSettingsCache(): void {
  settingsCache = null;
  cacheTimestamp = 0;
}

/**
 * Pre-fetch settings on app load
 * Call this early to warm the cache
 */
export function prefetchSettings(): void {
  fetchSystemSettings().catch(console.error);
}
